import React from "react";

const EnterpriseIntegrationLayout = () => {
    return (
        <div className="bg-white">

            {/* ================= HERO ================= */}
            <section
                className="relative h-[520px] flex items-center text-white"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(10,15,25,.85), rgba(10,15,25,.85)), url(https://images.unsplash.com/photo-1523961131990-5ea7c61b2107)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="relative w-full px-6 md:px-16 lg:px-24 py-24 text-white text-left">
                    <span className="uppercase text-sm tracking-wide text-orange-400">
                        Services
                    </span>
                    <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-4">
                        Enterprise Integration
                    </h1>
                    <p className="max-w-2xl text-lg text-gray-200 mb-6">
                        Enhance customer experience, unlock enterprise insights, and break
                        down operational silos with modern integration strategies.
                    </p>
                    <button className="bg-orange-500 hover:bg-orange-600 px-6 py-3 rounded-md font-semibold">
                        Consult Our Experts
                    </button>
                </div>
            </section>

            {/* ================= FEATURED INSIGHT ================= */}
            <section className="py-12 border-b">
                <div className="max-w-7xl mx-auto px-6">
                    <p className="text-sm text-orange-600 font-medium mb-2">
                        Featured Insight
                    </p>
                    <p className="text-lg font-semibold text-gray-800">
                        Leveraging AI-Driven Integration for Intelligent Decision Making →
                    </p>
                </div>
            </section>

            {/* ================= OVERVIEW ================= */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2">
                        <p className="text-gray-700 mb-4">
                            Modern enterprises rely on a complex ecosystem of applications,
                            data platforms, and digital services. Enterprise integration
                            ensures seamless connectivity, real-time data exchange, and
                            scalable system interoperability.
                        </p>
                        <p className="text-gray-700">
                            Our integration services enable organizations to modernize
                            application landscapes, streamline processes, and build resilient,
                            API-driven architectures.
                        </p>
                    </div>
                </div>
            </section>

            {/* ================= STRATEGY ================= */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-10">
                    <div className="md:col-span-2">
                        <h2 className="text-3xl font-bold mb-4">
                            Enterprise Integration Strategy & Advisory
                        </h2>
                        <p className="text-gray-600 mb-6">
                            Align integration architecture with business objectives through
                            platform evaluation, roadmap design, and scalable implementation
                            strategies.
                        </p>
                        <button className="bg-orange-500 text-white px-6 py-3 rounded-md">
                            Learn more about our service offerings
                        </button>
                    </div>

                    <div className="bg-[#020617] text-white p-6 rounded-xl">
                        <h4 className="font-semibold mb-4">Integration Services</h4>
                        <ul className="text-sm space-y-2 text-gray-300">
                            <li>• API & Event-Driven Integration</li>
                            <li>• Managed Integration Services</li>
                            <li>• Data & Application Migration</li>
                            <li>• Integration Accelerators</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* ================= PARTNERSHIP BANNER ================= */}
            <section
                className="py-24 text-white"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(10,10,20,.9), rgba(10,10,20,.9)), url(https://images.unsplash.com/photo-1518770660439-4636190af475)",
                    backgroundSize: "cover",
                }}
            >
                <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
                    <h2 className="text-2xl md:text-3xl font-bold max-w-xl">
                        Strategic partnerships delivering next-generation integration
                        excellence
                    </h2>
                    <button className="border border-white px-6 py-3 rounded-md">
                        Learn more
                    </button>
                </div>
            </section>

            {/* ================= WHY US ================= */}
            <section className="py-20 bg-[#fde7d9]">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
                    {[
                        {
                            title: "Deep Integration Expertise",
                            text: "Proven experience integrating complex enterprise ecosystems.",
                        },
                        {
                            title: "Automation & IP-Led",
                            text: "Accelerate delivery with reusable frameworks and accelerators.",
                        },
                        {
                            title: "Extensive Partner Ecosystem",
                            text: "Certified expertise across leading integration platforms.",
                        },
                    ].map((item) => (
                        <div key={item.title}>
                            <h4 className="font-semibold mb-2">{item.title}</h4>
                            <p className="text-sm text-gray-700">{item.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ================= CLIENT SUCCESS ================= */}
            <section className="py-20 bg-[#020617] text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-10">Client Success</h2>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                img: "https://images.unsplash.com/photo-1555949963-aa79dcee981c",
                                title: "Improving IAM efficiency for a global enterprise",
                                cta: "Read the story →",
                            },
                            {
                                img: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4",
                                title: "Reducing security risk through platform modernization",
                                cta: "View case study →",
                            },
                            {
                                img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d",
                                title: "Enhancing compliance and operational resilience",
                                cta: "Explore success →",
                            },
                        ].map((item, i) => (
                            <div
                                key={i}
                                className="bg-white text-black rounded-xl overflow-hidden"
                            >
                                <img
                                    src={item.img}
                                    alt={item.title}
                                    className="w-full h-48 object-cover"
                                    loading="lazy"
                                />

                                <div className="p-5">
                                    <h4 className="font-semibold mb-2 text-lg">
                                        {item.title}
                                    </h4>

                                    <button className="text-orange-500 font-semibold">
                                        {item.cta}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* ================= PARTNERS ================= */}
            <section className="py-16 border-b">
                <div className="max-w-7xl mx-auto px-6">
                    <h3 className="text-2xl font-bold mb-6">
                        Learn About Our Key Partners
                    </h3>
                    <div className="flex flex-wrap gap-8 items-center">
                        {["MuleSoft", "Boomi", "AWS", "Azure", "Google Cloud"].map((p) => (
                            <span key={p} className="text-gray-500 font-semibold text-lg">
                                {p}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= NEWS ================= */}
            {/* <section className="py-20 bg-[#020617] text-white">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-10">News & Insights</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white text-black rounded-xl p-5">
                                <h4 className="font-semibold mb-2">
                                    Enterprise Integration Trends Shaping the Future
                                </h4>
                                <button className="text-orange-500 font-semibold">
                                    Read more →
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* ================= CONTACT ================= */}
            {/* <section className="py-20">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl font-bold mb-8">Contact Us</h2>
                    <form className="grid md:grid-cols-2 gap-6">
                        <input className="border p-3 rounded" placeholder="First name" />
                        <input className="border p-3 rounded" placeholder="Last name" />
                        <input className="border p-3 rounded" placeholder="Work email" />
                        <input className="border p-3 rounded" placeholder="Company" />
                        <textarea
                            className="border p-3 rounded md:col-span-2"
                            placeholder="How can we help?"
                            rows="4"
                        />
                        <button className="bg-orange-500 text-white px-6 py-3 rounded-md font-semibold">
                            Submit
                        </button>
                    </form>
                </div>
            </section> */}

        </div>
    );
};

export default EnterpriseIntegrationLayout;
